import { Skeleton, useTheme } from "@mui/material";
import * as React from "react";

import { useNavigation } from "@/providers";
import {
  RobotoStyledPopoverMenu as Menu,
  RobotoMenuItem as MenuItem,
  RobotoLinkButton as Link,
  RobotoLink,
} from "@/shared/components";
import { useDataset } from "@/shared/domain/datasets/hooks";
import { usePopupState } from "@/shared/hooks";
import { Goto } from "@/shared/navigation";

import CopyToClipboardButton from "../../../CopyToClipboardButton";

import styles from "./DatasetInfo.module.css";

interface DatasetInfoProps {
  datasetIds: string[];
  isDatasetsLoading: boolean;
}

/**
 * Displays dataset information - either a single dataset name or count of multiple datasets.
 */
export function DatasetInfo({
  datasetIds,
  isDatasetsLoading,
}: DatasetInfoProps) {
  if (isDatasetsLoading) {
    return <Skeleton animation="wave" variant="text" width="200px" />;
  }

  if (datasetIds.length === 1) {
    return <SingleDatasetInfo datasetId={datasetIds[0]} />;
  }

  if (datasetIds.length > 1) {
    return <MultipleDatasetsInfo datasetIds={datasetIds} />;
  }

  return null;
}

function SingleDatasetInfo({ datasetId }: { datasetId: string }) {
  const theme = useTheme();
  const datasetQuery = useDataset(datasetId);
  const dataset = datasetQuery.data;

  const [isHovering, setIsHovering] = React.useState(false);

  const displayText = dataset?.name
    ? `${datasetId} (${dataset.name})`
    : datasetId;

  return (
    <div
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      className={styles.datasetInfoContainer}
    >
      <RobotoLink
        className={styles.datasetInfo}
        sx={{
          color: theme.palette.text.secondary,
          marginRight: theme.spacing(0.5),
        }}
        title={displayText}
        to={Goto.createDatasetRoute(datasetId)}
        underline="hover"
      >
        {displayText}
      </RobotoLink>
      <CopyToClipboardButton
        text={datasetId}
        sx={{
          opacity: isHovering ? 1 : 0,
          transition: "opacity 0.2s",
          flexShrink: 0,
        }}
      />
    </div>
  );
}

function MultipleDatasetsInfo({ datasetIds }: { datasetIds: string[] }) {
  const theme = useTheme();
  const popup = usePopupState();

  return (
    <>
      <Menu anchorEl={popup.anchor} open={popup.isOpen} onClose={popup.close}>
        {datasetIds.map((id) => (
          <DatasetMenuItem key={id} datasetId={id} />
        ))}
      </Menu>
      <div>
        <Link
          component="button"
          underline="hover"
          color={theme.palette.text.secondary}
          className={styles.datasetInfo}
          onClick={(event) => popup.open({ anchorEl: event.currentTarget })}
          title={datasetIds.join(", ")}
        >
          {datasetIds.length} Datasets
        </Link>
      </div>
    </>
  );
}

function DatasetMenuItem({ datasetId }: { datasetId: string }) {
  const { goto } = useNavigation();
  const datasetQuery = useDataset(datasetId);

  let displayText = datasetId;

  if (datasetQuery.data?.name) {
    displayText = `${datasetId} (${datasetQuery.data.name})`;
  }

  return (
    <MenuItem
      eventName="DatasetOpened"
      eventProperties={{ datasetId }}
      onClick={() => goto.dataset({ datasetId })}
    >
      {displayText}
    </MenuItem>
  );
}
